export interface ExactObjectUploadInput {
  readonly url: string;
  readonly file: Blob;
  readonly requiredHeaders: Readonly<Record<string, string>>;
  readonly signal?: AbortSignal;
  /**
   * Bounds a stalled request. Invalid XMLHttpRequest timeout values use five
   * minutes rather than disabling or breaking the upload.
   */
  readonly timeoutMs?: number;
  readonly onProgress?: (progress: {
    readonly loadedBytes: number;
    readonly totalBytes: number;
    readonly percent: number;
  }) => void;
}

const DEFAULT_UPLOAD_TIMEOUT_MS = 5 * 60_000;
const MAX_XHR_TIMEOUT_MS = 0xffff_ffff;

/**
 * Data-blind browser transport for an exact object-store write grant. The
 * owner feature supplies only opaque grant data and receives byte progress;
 * this helper has no tenant, document, product, or auth knowledge.
 */
export function uploadExactObject(
  input: ExactObjectUploadInput,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (input.signal?.aborted) {
      reject(new DOMException("Upload cancelled", "AbortError"));
      return;
    }
    const request = new XMLHttpRequest();
    let settled = false;
    const finish = (callback: () => void): void => {
      if (settled) return;
      settled = true;
      input.signal?.removeEventListener("abort", abort);
      callback();
    };
    const abort = (): void => request.abort();

    request.open("PUT", input.url, true);
    request.timeout = normalizeUploadTimeout(input.timeoutMs);
    for (const [name, value] of Object.entries(input.requiredHeaders)) {
      request.setRequestHeader(name, value);
    }
    request.upload.addEventListener("progress", (event) => {
      const totalBytes = event.lengthComputable ? event.total : input.file.size;
      input.onProgress?.({
        loadedBytes: event.loaded,
        totalBytes,
        percent:
          totalBytes > 0
            ? Math.min(100, Math.round((event.loaded / totalBytes) * 100))
            : 0,
      });
    });
    request.addEventListener("load", () => {
      if (request.status >= 200 && request.status < 300) {
        finish(resolve);
      } else {
        finish(() => reject(new ExactObjectUploadError(request.status)));
      }
    });
    request.addEventListener("error", () => {
      finish(() => reject(new ExactObjectUploadError(0)));
    });
    request.addEventListener("abort", () => {
      finish(() => reject(new DOMException("Upload cancelled", "AbortError")));
    });
    request.addEventListener("timeout", () => {
      finish(() => reject(new ExactObjectUploadError(0)));
    });

    input.signal?.addEventListener("abort", abort, { once: true });
    request.send(input.file);
  });
}

function normalizeUploadTimeout(timeoutMs: number | undefined): number {
  return timeoutMs !== undefined &&
    Number.isFinite(timeoutMs) &&
    Number.isInteger(timeoutMs) &&
    timeoutMs > 0 &&
    timeoutMs <= MAX_XHR_TIMEOUT_MS
    ? timeoutMs
    : DEFAULT_UPLOAD_TIMEOUT_MS;
}

export class ExactObjectUploadError extends Error {
  constructor(readonly status: number) {
    super(
      status > 0 ? `Object upload failed (${status})` : "Object upload failed",
    );
    this.name = "ExactObjectUploadError";
  }
}
